import * as React from 'react';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Tag from 'antd/lib/tag';
import { CheckOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { DestinationSummary, ReleaseStatus, RunwayResponseType } from '@/types';
import { antGrey } from '../styles/globalStyles';
import { globalMessage } from '@/utils/globalMessage';
import { WorkflowRunUpdatable } from '@/actions/workflowRun';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectRelease,
    selectWorkflowId,
    selectWorkflowRunId,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { DestinationCounter } from '../appHome/DestinationCounter';

export interface EditDestinationsModalProps {
    visible: boolean;
    onClose: () => void;
    appId: string;
    orgId: string;
    releaseId: string;
}

type SelectionMode = 'include' | 'exclude';

export const EditDestinationsModal: React.FC<EditDestinationsModalProps> = ({
    visible,
    onClose,
    appId,
    orgId,
    releaseId,
}) => {
    const [mode, setMode] = React.useState<SelectionMode>('include');
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const destinationSummaries = useSelector(
        (state: RootState) => selectRelease(state, releaseId).destinationSummaries,
    );

    const destinationSummaryByStepConfigId = React.useMemo(() => {
        const map = new Map<string, DestinationSummary>();
        for (const s of destinationSummaries ?? []) {
            map.set(s.stepConfigId, s);
        }
        return map;
    }, [destinationSummaries]);

    const destinationInfoByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const destinationRows = React.useMemo(
        () => Object.values(destinationInfoByStepConfigId),
        [destinationInfoByStepConfigId],
    );

    const allDestinationStepConfigIds = React.useMemo(
        () => destinationRows.map((r) => r.stepConfigId),
        [destinationRows],
    );

    const initiallyIncludedStepConfigIds = React.useMemo(() => {
        const included: string[] = [];
        for (const row of destinationRows) {
            const status =
                destinationSummaryByStepConfigId.get(row.stepConfigId)?.destinationStatus ?? ReleaseStatus.Active;
            if (status !== ReleaseStatus.Skipped) {
                included.push(row.stepConfigId);
            }
        }
        return included;
    }, [destinationRows, destinationSummaryByStepConfigId]);

    const hasInitializedRef = React.useRef(false);
    React.useEffect(() => {
        if (!visible) {
            hasInitializedRef.current = false;
            return;
        }
        if (!hasInitializedRef.current && destinationRows.length > 0) {
            hasInitializedRef.current = true;
            setMode('include');
            setSelectedIds(initiallyIncludedStepConfigIds);
        }
    }, [visible, destinationRows.length, initiallyIncludedStepConfigIds]);

    const includedStepConfigIds = React.useMemo(() => {
        if (mode === 'include') {
            return selectedIds;
        }
        const excludedSet = new Set(selectedIds);
        return allDestinationStepConfigIds.filter((id) => !excludedSet.has(id));
    }, [mode, selectedIds, allDestinationStepConfigIds]);

    const [updateDestinationStatuses, isUpdateLoading, updateResponseType, updateError] = useCallUpdatable(
        WorkflowRunUpdatable.UpdateDestinationStatuses,
        {
            orgId,
            workflowId: workflowId!,
            workflowRunId: workflowRunId!,
        },
        allDestinationStepConfigIds.join(','),
    );

    // Ensures the success toast/onClose only fire once per successful update,
    // since updateResponseType remains `Success` in Redux after the call completes
    // and would otherwise re-trigger the effect on every parent re-render.
    const hasHandledSuccessRef = React.useRef(false);

    React.useEffect(() => {
        if (visible) {
            hasHandledSuccessRef.current = false;
        }
    }, [visible]);

    React.useEffect(() => {
        if (updateResponseType === RunwayResponseType.Success && !hasHandledSuccessRef.current) {
            hasHandledSuccessRef.current = true;
            globalMessage.success('Destinations updated successfully');
            onClose();
        }
    }, [updateResponseType, onClose]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    const handleModeChange = (newMode: SelectionMode) => {
        if (newMode === mode) {
            return;
        }
        const includedSet = new Set(includedStepConfigIds);
        setSelectedIds(
            newMode === 'include' ? includedStepConfigIds : (
                allDestinationStepConfigIds.filter((id) => !includedSet.has(id))
            ),
        );
        setMode(newMode);
    };

    const handleConfirm = () => {
        const includedSet = new Set(includedStepConfigIds);
        const initialIncludedSet = new Set(initiallyIncludedStepConfigIds);

        const destinationUpdates: Array<{ destinationStepConfigId: string; status: ReleaseStatus }> = [];
        destinationRows.forEach((row) => {
            const wasIncluded = initialIncludedSet.has(row.stepConfigId);
            const isNowIncluded = includedSet.has(row.stepConfigId);

            if (wasIncluded !== isNowIncluded) {
                destinationUpdates.push({
                    destinationStepConfigId: row.stepConfigId,
                    status: isNowIncluded ? ReleaseStatus.Active : ReleaseStatus.Skipped,
                });
            }
        });

        if (destinationUpdates.length === 0) {
            onClose();
            return;
        }

        updateDestinationStatuses({ destinationUpdates });
    };

    const destinationOptions = React.useMemo(
        () =>
            destinationRows.map((row) => ({
                label: row.name,
                value: row.stepConfigId,
                destination: row,
            })),
        [destinationRows],
    );

    const destinationByStepConfigId = React.useMemo(() => {
        const map = new Map<string, (typeof destinationRows)[number]>();
        for (const row of destinationRows) {
            map.set(row.stepConfigId, row);
        }
        return map;
    }, [destinationRows]);

    const selectedCount = selectedIds.length;
    const includedCount = includedStepConfigIds.length;
    const totalCount = destinationRows.length;
    const willSkipAll = includedCount === 0;

    const okText =
        willSkipAll ?
            'Skip all destinations for this release'
        :   `Target ${includedCount} of ${totalCount} destinations for this release`;

    return (
        <Modal
            title="Edit destinations"
            open={visible}
            onCancel={onClose}
            okButtonProps={{
                loading: isUpdateLoading,
                icon: <CheckOutlined />,
            }}
            okText={okText}
            onOk={handleConfirm}
            cancelText="Cancel"
            destroyOnClose
            width={720}
        >
            <Flex justify="space-between" align="center" gap={12} style={{ marginBottom: 8 }}>
                <Text style={{ color: antGrey.grey7 }}>
                    Choose which destinations to include or exclude for this release.
                </Text>
                <Button
                    type="link"
                    size="small"
                    disabled={selectedCount === 0}
                    onClick={() => setSelectedIds([])}
                    style={{ padding: 0, height: 'fit-content', flexShrink: 0 }}
                >
                    Clear all
                </Button>
            </Flex>

            <Flex gap={12} align="flex-start" style={{ marginBottom: willSkipAll ? 16 : 0 }}>
                <Select<SelectionMode>
                    value={mode}
                    onChange={handleModeChange}
                    style={{ width: 130, flexShrink: 0 }}
                    options={[
                        { label: 'Include', value: 'include' },
                        { label: 'Exclude', value: 'exclude' },
                    ]}
                />
                <Select
                    mode="multiple"
                    value={selectedIds}
                    onChange={setSelectedIds}
                    options={destinationOptions}
                    optionRender={(option) => {
                        const destination = option.data.destination;
                        return (
                            <Flex align="center" gap={8}>
                                <DestinationCounter destinations={[destination]} size={20} />
                                <Text>{destination.name}</Text>
                            </Flex>
                        );
                    }}
                    tagRender={({ value, closable, onClose }) => {
                        const destination = destinationByStepConfigId.get(value as string);
                        return (
                            <Tag
                                closable={closable}
                                onClose={onClose}
                                onMouseDown={(e) => {
                                    // Prevent the tag's mousedown from stealing focus / reopening the dropdown.
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    marginInlineEnd: 4,
                                }}
                            >
                                {destination && (
                                    <DestinationCounter destinations={[destination]} size={16} hideTooltip />
                                )}
                                {destination?.name ?? (value as string)}
                            </Tag>
                        );
                    }}
                    optionFilterProp="label"
                    placeholder={
                        mode === 'include' ? 'Select destinations to include...' : 'Select destinations to exclude...'
                    }
                    allowClear
                    showSearch
                    maxTagCount="responsive"
                    style={{ flex: 1, minWidth: 0 }}
                />
            </Flex>

            {willSkipAll && (
                <Alert
                    type="warning"
                    showIcon
                    message="No destinations are included. Submitting will skip all destinations for this release."
                />
            )}
        </Modal>
    );
};
